import { useEffect, useState, useMemo } from "react";
import api, { apiError } from "@/lib/api";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import { fmtDate, todayISO } from "@/lib/format";
import { PageHeader, StatCard, Empty, Cell, Segmented } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Truck, Send, Pencil, CheckCircle2, Clock, XCircle, Package, FileText, Eye, RefreshCw, Search, User, AlertTriangle, Loader2 } from "lucide-react";
import { DatePicker } from "@/components/DatePicker";
import { Combobox } from "@/components/Combobox";
import { contactOptions } from "@/lib/contacts";
import ProductPicker from "@/components/ProductPicker";
import QnbIncomingPanel from "@/components/QnbIncomingPanel";
import IrsaliyeQnbDialog from "@/components/IrsaliyeQnbDialog";
import DriverVehiclePicker from "@/components/DriverVehiclePicker";
import HtmlPreviewDialog, { fetchPreviewUrl } from "@/components/HtmlPreviewDialog";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { confirm } from "@/components/ConfirmDialog";

const UNITS = ["ADET", "METRE", "KG", "KOLİ", "PAKET", "TAKIM"];

// Satır durumu: taslak → gönderildi (QNB'de) → ulaştı (GİB/alıcı) | hata
const statusOf = (irs) => {
  const q = irs.qnb || {};
  const st = q.status || {};
  if (q.belge_oid || q.success) {
    if (st.durum === 2 || q.success === false) return { key: "hata", label: "Hata", cls: "bg-red-100 text-red-700 border-red-200", stripe: "bg-red-500", icon: XCircle };
    if (st.ulasti || irs.status === "ulasti") {
      if (q.unregistered_receiver) return { key: "ulasti", label: "Ulaştı · Nihai Tüketici", cls: "bg-amber-100 text-amber-800 border-amber-200", stripe: "bg-amber-500", icon: AlertTriangle };
      return { key: "ulasti", label: "Alıcıya ulaştı", cls: "bg-emerald-600 text-white border-emerald-600", stripe: "bg-emerald-500", icon: CheckCircle2 };
    }
    return { key: "gonderildi", label: "QNB'de işleniyor", cls: "bg-sky-100 text-sky-700 border-sky-200", stripe: "bg-sky-500", icon: Send };
  }
  return { key: "taslak", label: "Taslak", cls: "bg-muted text-muted-foreground border-border", stripe: "bg-slate-300", icon: Clock };
};
const FILTERS = [["all", "Tümü"], ["taslak", "Taslak"], ["gonderildi", "İşleniyor"], ["ulasti", "Ulaştı"], ["hata", "Hata"]];

const F = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</Label>
    {children}
  </div>
);

export default function Irsaliyeler() {
  const [rows, setRows] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(false);
  const [editId, setEditId] = useState(null);
  const [fromDlg, setFromDlg] = useState(false);
  const [xmlDlg, setXmlDlg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("giden");
  const [sel, setSel] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [htmlPrev, setHtmlPrev] = useState(null);

  const blank = {
    contact_title: "", receiver_vkn: "", despatch_date: todayISO(), despatch_time: "",
    delivery_address: "", receiver_city: "", receiver_district: "",
    carrier_plate: "", driver_name: "", driver_surname: "", driver_tckn: "",
    note: "", items: [{ name: "", description: "", quantity: 1, unit: "ADET" }],
  };
  const [form, setForm] = useState(blank);
  const [params, setParams] = useSearchParams();
  const [defaults, setDefaults] = useState({});

  const load = (silent = false) => {
    if (!silent) setLoading(true);
    return api.get("/irsaliyeler").then((r) => { setRows((p) => (JSON.stringify(p) === JSON.stringify(r.data) ? p : r.data)); return r.data; }).catch((e) => { if (!silent) toast.error(apiError(e)); return []; }).finally(() => { if (!silent) setLoading(false); });
  };
  useEffect(() => {
    load().then((list) => {
      // /irsaliyeler?edit=ID → faturadan oluşturulan taslağı düzenleme için aç
      const editId = params.get("edit");
      if (editId) { const irs = (list || []).find((x) => x.id === editId); if (irs) openEdit(irs); setParams({}, { replace: true }); }
    });
    api.get("/irsaliyeler/defaults").then((r) => setDefaults(r.data || {})).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/invoices?type=satis").then((r) => setInvoices(r.data)).catch(() => {});
  }, []);
  // Yalnızca QNB'de işlenmekte olan irsaliye varsa sessizce yenile (ekran titremesi yok)
  const hasPending = useMemo(() => rows.some((r) => statusOf(r).key === "gonderildi"), [rows]);
  useAutoRefresh(() => load(true), { interval: 15000, enabled: tab === "giden" && hasPending });

  const stats = useMemo(() => {
    const keys = rows.map((r) => statusOf(r).key);
    const n = (k) => keys.filter((x) => x === k).length;
    const month = todayISO().slice(0, 7);
    return { total: rows.length, draft: n("taslak"), processing: n("gonderildi"), arrived: n("ulasti"), error: n("hata"), thisMonth: rows.filter((r) => String(r.despatch_date || "").startsWith(month)).length };
  }, [rows]);
  const [invSearch, setInvSearch] = useState("");
  const invChoices = useMemo(() => {
    const q = invSearch.trim().toLocaleLowerCase("tr");
    return invoices.filter((inv) => !q || [inv.contact_title, inv.invoice_no].some((v) => String(v || "").toLocaleLowerCase("tr").includes(q))).slice(0, 60);
  }, [invoices, invSearch]);
  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr");
    return rows.filter((r) => (filter === "all" || statusOf(r).key === filter) && (!q || [r.contact_title, r.irsaliye_no, r.carrier_plate, r.receiver_vkn].some((v) => String(v || "").toLocaleLowerCase("tr").includes(q))));
  }, [rows, filter, search]);

  const openNew = () => { setForm({ ...blank, carrier_plate: defaults.carrier_plate || "", driver_name: defaults.driver_name || "", driver_surname: defaults.driver_surname || "", driver_tckn: defaults.driver_tckn || "" }); setEditId(null); setDlg(true); };
  const openEdit = (irs) => {
    setForm({
      ...blank, ...irs,
      items: (irs.items || []).length ? irs.items.map((x) => ({ ...x })) : blank.items,
    });
    setEditId(irs.id);
    setDlg(true);
  };

  const pickContact = (title) => {
    const c = contacts.find((x) => x.title === title);
    setForm((f) => ({ ...f, contact_title: title, receiver_vkn: c?.tax_no || f.receiver_vkn, delivery_address: f.delivery_address || c?.address || "" }));
  };

  const addRow = () => setForm({ ...form, items: [...form.items, { name: "", description: "", quantity: 1, unit: "ADET" }] });
  const rmRow = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const setRow = (i, k, v) => setForm({ ...form, items: form.items.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)) });
  const addFromStock = (p) => {
    const row = { name: p.name, description: p.description || "", quantity: 1, unit: p.unit || "ADET", product_id: p.id };
    setForm((f) => {
      const items = [...f.items];
      const emptyIdx = items.findIndex((it) => !it.name.trim());
      if (emptyIdx >= 0) items[emptyIdx] = row; else items.push(row);
      return { ...f, items };
    });
  };

  const buildFromInvoice = (inv) => {
    setForm({
      ...blank,
      contact_title: inv.contact_title || "",
      receiver_vkn: (contacts.find((c) => c.title === inv.contact_title)?.tax_no) || "",
      delivery_address: contacts.find((c) => c.title === inv.contact_title)?.address || "",
      note: inv.invoice_no ? `${inv.invoice_no} nolu faturaya istinaden` : "",
      items: (inv.items || []).length
        ? inv.items.map((x) => ({ name: x.name, description: x.description || "", quantity: Number(x.quantity) || 1, unit: x.unit || "ADET", product_id: x.product_id || "" }))
        : blank.items,
    });
    setEditId(null);
    setFromDlg(false);
    setDlg(true);
  };

  const save = async () => {
    if (!form.contact_title.trim()) return toast.error("Cari ünvanı gerekli");
    if (!form.despatch_date) return toast.error("Fiili sevk tarihi gerekli");
    const items = form.items.filter((i) => i.name.trim()).map((i) => ({
      name: i.name.trim(), description: i.description || "", quantity: Number(i.quantity) || 0,
      unit: i.unit || "ADET", product_id: i.product_id || "",
    }));
    if (!items.length) return toast.error("En az bir kalem girin");
    const payload = {
      contact_title: form.contact_title.trim(), receiver_vkn: form.receiver_vkn || "",
      despatch_date: form.despatch_date, despatch_time: form.despatch_time || "",
      delivery_address: form.delivery_address || "", receiver_city: form.receiver_city || "",
      receiver_district: form.receiver_district || "", carrier_plate: form.carrier_plate || "",
      driver_name: form.driver_name || "", driver_surname: form.driver_surname || "",
      driver_tckn: form.driver_tckn || "", note: form.note || "", items,
    };
    setBusy(true);
    try {
      if (editId) await api.put(`/irsaliyeler/${editId}`, payload);
      else await api.post("/irsaliyeler", payload);
      toast.success(editId ? "İrsaliye güncellendi" : "İrsaliye oluşturuldu");
      setDefaults({ carrier_plate: payload.carrier_plate, driver_name: payload.driver_name, driver_surname: payload.driver_surname, driver_tckn: payload.driver_tckn });
      setDlg(false); setForm(blank); setEditId(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const del = async (irs) => {
    if (!await confirm(`"${irs.contact_title}" irsaliyesi silinsin mi?`)) return;
    try { await api.delete(`/irsaliyeler/${irs.id}`); toast.success("Silindi"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const preview = async (irs) => {
    try {
      if (irs.qnb?.ettn && irs.qnb?.success) {
        const url = await fetchPreviewUrl(`/irsaliyeler/${irs.id}/qnb-pdf`);
        setHtmlPrev({ url, title: irs.irsaliye_no || irs.contact_title });
        return;
      }
      // Taslak: GİB e-İrsaliye görünümü (QNB ublOnizleme / yerel GİB şablonu) — belge oluşturmaz
      try {
        const { data } = await api.post(`/irsaliyeler/${irs.id}/qnb-gib-preview`);
        setHtmlPrev({ url: data.url, title: `${irs.irsaliye_no || irs.contact_title} · GİB görünümü (taslak)` });
        return;
      } catch (e) { toast.warning(`GİB görünümü alınamadı — UBL gösteriliyor · ${apiError(e)}`); }
      const { data } = await api.get(`/irsaliyeler/${irs.id}/qnb-preview`);
      setXmlDlg({ title: irs.irsaliye_no || irs.contact_title, xml: data.xml });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6 stagger-in" data-testid="irsaliyeler-page">
      <PageHeader title="e-İrsaliye" badge="e-Belge" subtitle="Sevk irsaliyesi oluşturun, alıcı mükellef kontrolüyle QNB üzerinden gönderin; GİB durumunu ve gelen e-İrsaliyeleri takip edin" testid="irsaliye-header">
        {tab === "giden" && <>
          <Button variant="outline" onClick={() => setFromDlg(true)} data-testid="from-invoice-btn"><FileText className="h-4 w-4 mr-1.5" />Faturadan Oluştur</Button>
          <Button onClick={openNew} data-testid="new-irsaliye-btn"><Plus className="h-4 w-4 mr-1.5" />Yeni İrsaliye</Button>
        </>}
      </PageHeader>

      <div data-testid="irs-tabs">
        <Segmented value={tab} onChange={setTab} testid="irs-tab" options={[["giden", "Giden İrsaliyeler", rows.length], ["gelen", "Gelen e-İrsaliyeler (QNB)"]]} />
      </div>

      {tab === "gelen" ? <QnbIncomingPanel defaultType="IRSALIYE" showTypeToggle={false} /> : (<>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="irs-kpis">
        <StatCard label="Toplam İrsaliye" value={stats.total} icon={Truck} sub={`${stats.thisMonth} bu ay sevk`} testid="irs-kpi-total" onClick={() => setFilter("all")} active={filter === "all"} />
        <StatCard label="Taslak" value={stats.draft} icon={Clock} tone="primary" sub="gönderilmeyi bekliyor" testid="irs-kpi-draft" onClick={() => setFilter("taslak")} active={filter === "taslak"} />
        <StatCard label="QNB'de İşleniyor" value={stats.processing} icon={Send} tone="blue" sub="GİB yanıtı bekleniyor" testid="irs-kpi-processing" onClick={() => setFilter("gonderildi")} active={filter === "gonderildi"} />
        <StatCard label="Alıcıya Ulaştı" value={stats.arrived} icon={CheckCircle2} tone="success" sub="GİB 1200" testid="irs-kpi-arrived" onClick={() => setFilter("ulasti")} active={filter === "ulasti"} />
        <StatCard label="Hatalı" value={stats.error} icon={XCircle} tone={stats.error ? "danger" : "default"} sub={stats.error ? "yeniden gönderim gerekli" : "sorun yok"} testid="irs-kpi-error" onClick={() => setFilter("hata")} active={filter === "hata"} />
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="irs-toolbar">
        <Segmented value={filter} onChange={setFilter} testid="irs-filter" size="xs"
          options={FILTERS.map(([k, l]) => [k, l, k === "all" ? rows.length : rows.filter((r) => statusOf(r).key === k).length])} />
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-72">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari, irsaliye no, plaka ara..." className="pl-9 h-9" data-testid="irs-search" />
          </div>
          <Button variant="outline" size="sm" className="h-9" onClick={() => load(true)} title={hasPending ? "İşlenen irsaliye var: 15 sn'de bir otomatik güncellenir" : "Listeyi yenile"} data-testid="irs-refresh-btn">
            <RefreshCw className={`h-4 w-4 ${hasPending ? "animate-spin [animation-duration:3s]" : ""}`} /><span className="hidden sm:inline ml-1.5">Yenile</span>
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground px-1" data-testid="irs-list-summary">
        <span><b className="text-foreground tabular-nums">{filtered.length}</b> irsaliye listeleniyor</span>
        <span className="tabular-nums">Toplam kalem: <b className="text-foreground">{filtered.reduce((s, r) => s + (r.items || []).length, 0)}</b></span>
      </div>

      {loading ? (
        <Empty title="Yükleniyor..." />
      ) : rows.length === 0 ? (
        <Empty title="Henüz irsaliye yok" desc="Yeni İrsaliye veya Faturadan Oluştur ile başlayın." />
      ) : filtered.length === 0 ? (
        <Empty title="Eşleşen irsaliye yok" desc="Filtreyi veya aramayı değiştirin." />
      ) : (
        <div className="space-y-2.5" data-testid="irs-list">
          {filtered.map((irs) => {
            const st = statusOf(irs);
            const StIcon = st.icon;
            const q = irs.qnb || {};
            const locked = st.key === "gonderildi" || st.key === "ulasti";
            const names = (irs.items || []).map((x) => x.name).filter(Boolean);
            const driver = [irs.driver_name, irs.driver_surname].filter(Boolean).join(" ");
            return (
              <div key={irs.id} className="group relative rounded-xl border bg-card pl-5 pr-4 py-3.5 overflow-hidden transition-all hover:shadow-md hover:-translate-y-px" data-testid={`irsaliye-row-${irs.id}`}>
                <span className={`absolute left-0 top-0 bottom-0 w-1.5 ${st.stripe}`} />
                <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                  <div className="flex-1 grid grid-cols-2 md:grid-cols-5 gap-3 min-w-0">
                    <Cell label="Alıcı" className="col-span-2 md:col-span-1">
                      <div className="font-head font-black text-[15px] leading-tight truncate" title={irs.contact_title}>{irs.contact_title}</div>
                      <div className="text-[11px] text-muted-foreground font-mono truncate">{irs.receiver_vkn || "VKN/TCKN yok"}{(irs.receiver_district || irs.receiver_city) ? <span className="font-sans"> · {[irs.receiver_district, irs.receiver_city].filter(Boolean).join("/")}</span> : null}</div>
                    </Cell>
                    <Cell label="İrsaliye No">
                      {irs.irsaliye_no ? <div className="font-mono text-sm font-bold tabular-nums">{irs.irsaliye_no}</div> : <div className="text-xs italic text-muted-foreground">Gönderimde atanır</div>}
                      {irs.from_invoice_no && <div className="text-[10px] text-sky-700 font-semibold truncate" title={`${irs.from_invoice_no} nolu faturadan`} data-testid={`irs-from-invoice-${irs.id}`}><FileText className="h-3 w-3 inline mr-0.5 -mt-0.5" />Fatura {irs.from_invoice_no}</div>}
                      {irs.from_quote_no && <div className="text-[10px] text-primary font-semibold truncate" title={`${irs.from_quote_no} nolu tekliften`} data-testid={`irs-from-quote-${irs.id}`}><FileText className="h-3 w-3 inline mr-0.5 -mt-0.5" />Teklif {irs.from_quote_no}</div>}
                      {q.ettn && <div className="text-[10px] text-muted-foreground font-mono truncate" title={q.ettn}>ETTN {q.ettn.slice(0, 8)}…</div>}
                    </Cell>
                    <Cell label="Sevk · Kalem">
                      <div className="text-sm tabular-nums font-semibold">{fmtDate(irs.despatch_date)}{irs.despatch_time ? <span className="text-muted-foreground font-normal"> {irs.despatch_time}</span> : null}</div>
                      <div className="text-[11px] text-muted-foreground truncate" title={names.join(", ")}><Package className="h-3 w-3 inline mr-1 -mt-0.5" />{names.length} kalem{names.length ? ` · ${names.slice(0, 2).join(", ")}${names.length > 2 ? "…" : ""}` : ""}</div>
                    </Cell>
                    <Cell label="Taşıma">
                      {irs.carrier_plate ? <span className="inline-block font-mono text-xs font-bold bg-slate-900 text-white rounded px-1.5 py-0.5 tracking-wider">{irs.carrier_plate}</span> : <span className="text-xs text-muted-foreground">Plaka yok</span>}
                      <div className="text-[11px] text-muted-foreground truncate mt-0.5"><User className="h-3 w-3 inline mr-1 -mt-0.5" />{driver || "Şoför yok"}</div>
                    </Cell>
                    <Cell label="Durum">
                      <Badge variant="outline" className={`${st.cls} font-bold`} data-testid={`irsaliye-status-${irs.id}`}><StIcon className="h-3 w-3 mr-1" />{st.label}</Badge>
                      {q.status_label && <div className="text-[10px] text-muted-foreground mt-0.5 truncate" title={q.status_label} data-testid={`irsaliye-qnb-status-${irs.id}`}>{q.status_label}{q.status?.gib_kodu ? ` · GİB ${q.status.gib_kodu}` : ""}</div>}
                    </Cell>
                  </div>
                  <div className="flex items-center gap-1 lg:justify-end shrink-0 lg:pl-3 lg:border-l">
                    {locked ? (
                      <>
                        <span className={`inline-flex items-center h-8 px-2.5 rounded-md text-xs font-bold select-none ${st.key === "ulasti" ? "bg-emerald-600 text-white" : "bg-sky-100 text-sky-800"}`} data-testid={`irsaliye-sent-${irs.id}`}>
                          {st.key === "ulasti" ? <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> : <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}{st.key === "ulasti" ? "Gönderildi ✓" : "Gönderildi · işleniyor"}
                        </span>
                        <Button size="sm" variant="outline" className="h-8" onClick={() => setSel(irs)} data-testid={`irsaliye-open-${irs.id}`}><Eye className="h-3.5 w-3.5 mr-1" />Takip</Button>
                      </>
                    ) : (
                      <Button size="sm" variant={st.key === "hata" ? "destructive" : "default"} className="h-8" onClick={() => setSel(irs)} data-testid={`irsaliye-open-${irs.id}`}>
                        {st.key === "hata" ? <><RefreshCw className="h-3.5 w-3.5 mr-1" />Yeniden Gönder</> : <><Send className="h-3.5 w-3.5 mr-1" />Gönder</>}
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" className="h-8 w-8" title={locked ? "Belgeyi önizle" : "GİB görünümünü önizle (taslak)"} onClick={() => preview(irs)} data-testid={`irsaliye-preview-${irs.id}`}>{locked ? <FileText className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</Button>
                    {!locked && <Button size="icon" variant="ghost" className="h-8 w-8" title="Düzenle" onClick={() => openEdit(irs)} data-testid={`irsaliye-edit-${irs.id}`}><Pencil className="h-4 w-4" /></Button>}
                    {!locked && <Button size="icon" variant="ghost" className="h-8 w-8" title="Sil" onClick={() => del(irs)} data-testid={`irsaliye-del-${irs.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      </>)}

      <IrsaliyeQnbDialog irs={sel} onClose={() => setSel(null)} onDone={() => load(true)} />
      <HtmlPreviewDialog open={!!htmlPrev} title={htmlPrev?.title} url={htmlPrev?.url} onClose={() => setHtmlPrev(null)} />

      {/* Create / Edit dialog */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-[96vw] sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="irsaliye-dialog">
          <DialogHeader><DialogTitle className="font-head text-2xl">{editId ? "İrsaliyeyi Düzenle" : "Yeni Sevk İrsaliyesi"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <F label="Alıcı (Cari)">
              <Combobox
                options={contactOptions(contacts)}
                value={form.contact_title}
                onChange={pickContact}
                allowCustom
                placeholder="Cari seçin veya yazın"
                data-testid="irs-contact"
              />
            </F>
            <F label="Alıcı VKN / TCKN"><Input value={form.receiver_vkn} onChange={(e) => setForm({ ...form, receiver_vkn: e.target.value.replace(/\D/g, "").slice(0, 11) })} placeholder="10 veya 11 hane" data-testid="irs-vkn" /></F>
            <F label="Fiili Sevk Tarihi"><DatePicker value={form.despatch_date} onChange={(v) => setForm({ ...form, despatch_date: v })} data-testid="irs-date" /></F>
            <F label="Sevk Saati"><Input type="time" value={form.despatch_time} onChange={(e) => setForm({ ...form, despatch_time: e.target.value })} data-testid="irs-time" /></F>
            <F className="sm:col-span-2" label="Teslimat Adresi"><Input value={form.delivery_address} onChange={(e) => setForm({ ...form, delivery_address: e.target.value })} placeholder="Malın teslim edileceği adres" data-testid="irs-address" /></F>
            <F label="İlçe"><Input value={form.receiver_district} onChange={(e) => setForm({ ...form, receiver_district: e.target.value })} data-testid="irs-district" /></F>
            <F label="İl"><Input value={form.receiver_city} onChange={(e) => setForm({ ...form, receiver_city: e.target.value })} data-testid="irs-city" /></F>
          </div>

          <DriverVehiclePicker form={form} setForm={setForm} />

          <div className="space-y-2 min-w-0 mt-1">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5"><Package className="h-3.5 w-3.5" />Sevk Edilen Malzemeler</Label>
              <div className="flex items-center gap-2">
                <ProductPicker onSelect={addFromStock} label="Stoktan Ürün" testid="irs-product-picker" />
                <Button size="sm" variant="outline" onClick={addRow} data-testid="irs-add-row"><Plus className="h-3.5 w-3.5 mr-1" />Satır Ekle</Button>
              </div>
            </div>
            <div className="border rounded-lg overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]">
                <thead>
                  <tr className="bg-muted/60 text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="p-2 text-left w-8">#</th>
                    <th className="p-2 text-left min-w-[220px]">Malzeme Adı</th>
                    <th className="p-2 text-right w-24">Miktar</th>
                    <th className="p-2 text-left w-32">Birim</th>
                    <th className="p-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.items.map((it, i) => (
                    <tr key={i} className="border-t">
                      <td className="p-1.5 text-center text-muted-foreground tabular-nums">{i + 1}</td>
                      <td className="p-1.5"><Input className="h-9" placeholder="Malzeme adı" value={it.name} onChange={(e) => setRow(i, "name", e.target.value)} data-testid={`irs-item-name-${i}`} /></td>
                      <td className="p-1.5"><Input className="h-9 text-right tabular-nums" type="number" value={it.quantity} onChange={(e) => setRow(i, "quantity", e.target.value)} data-testid={`irs-item-qty-${i}`} /></td>
                      <td className="p-1.5">
                        <Select value={it.unit || "ADET"} onValueChange={(v) => setRow(i, "unit", v)}>
                          <SelectTrigger className="h-9" data-testid={`irs-item-unit-${i}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="p-1.5 text-center"><Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => rmRow(i)} data-testid={`irs-item-del-${i}`}><Trash2 className="h-4 w-4 text-destructive" /></Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <F label="Not / Açıklama"><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2} data-testid="irs-note" /></F>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)} data-testid="irs-cancel">Vazgeç</Button>
            <Button onClick={save} disabled={busy} data-testid="irs-save">{busy ? "Kaydediliyor..." : (editId ? "Güncelle" : "Kaydet")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* From invoice dialog */}
      <Dialog open={fromDlg} onOpenChange={(o) => { setFromDlg(o); if (!o) setInvSearch(""); }}>
        <DialogContent className="max-w-lg" data-testid="from-invoice-dialog">
          <DialogHeader><DialogTitle className="font-head text-xl">Faturadan İrsaliye Oluştur</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1">Bir satış faturası seçin; kalemler, cari ve teslimat adresi otomatik doldurulur.</p>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={invSearch} onChange={(e) => setInvSearch(e.target.value)} placeholder="Cari veya fatura no ara..." className="pl-9 h-9" autoFocus data-testid="from-invoice-search" />
          </div>
          <div className="max-h-[50vh] overflow-y-auto border rounded-xl divide-y">
            {invChoices.length === 0 ? (
              <p className="p-6 text-sm text-muted-foreground text-center">{invoices.length ? "Eşleşen fatura yok." : "Satış faturası bulunamadı."}</p>
            ) : invChoices.map((inv) => (
              <button key={inv.id} onClick={() => buildFromInvoice(inv)} className="w-full text-left px-4 py-3 hover:bg-muted/60 transition-colors group" data-testid={`from-invoice-${inv.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <span className="font-head font-bold text-sm truncate group-hover:text-primary transition-colors">{inv.contact_title}</span>
                  <span className="text-xs text-muted-foreground tabular-nums shrink-0">{fmtDate(inv.date)}</span>
                </div>
                <div className="flex items-center justify-between gap-3 mt-0.5">
                  <span className="text-xs text-muted-foreground font-mono">{inv.invoice_no || "No atanmadı"} · {(inv.items || []).length} kalem</span>
                  {(inv.irsaliye_ids || []).length > 0 && <span className="text-[10px] font-bold text-sky-700 inline-flex items-center gap-1"><Truck className="h-3 w-3" />İrsaliyesi var</span>}
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* UBL preview dialog */}
      <Dialog open={!!xmlDlg} onOpenChange={(o) => !o && setXmlDlg(null)}>
        <DialogContent className="max-w-[96vw] sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="irs-xml-dialog">
          <DialogHeader><DialogTitle className="font-head text-xl">UBL Önizleme · {xmlDlg?.title}</DialogTitle></DialogHeader>
          <pre className="text-[11px] bg-muted/50 p-3 rounded-lg overflow-x-auto whitespace-pre-wrap break-all" data-testid="irs-xml-content">{xmlDlg?.xml}</pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
